import type { ModulationLocationContractSchema } from '@libs/data-access/entities'
import { useQuery } from '@tanstack/react-query'
import moment from 'moment'

import type { UseAppQueryOptions } from '../../../../types'
import type {
  FetchModulationLocationContractOptions,
  FetchModulationLocationContractsOptions
} from '../modulation.location.contracts.api'
import {
  fetchModulationLocationContract,
  fetchModulationLocationContracts
} from '../modulation.location.contracts.api'
import { modulationLocationContractsCachekeys } from '../modulation.location.contracts.cacheKeys'

/**
 * Fetch modulation location contract
 */
export const useFetchModulationLocationContract = (
  request: FetchModulationLocationContractOptions,
  queryOptions?: UseAppQueryOptions<ModulationLocationContractSchema>
) => {
  return useQuery({
    ...queryOptions,
    queryKey: modulationLocationContractsCachekeys.one(
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      request?.pathParams?.modulation_location_contract_id
    ),
    queryFn: () => fetchModulationLocationContract(request)
  })
}

/**
 * Fetch modulation location contracts
 */
export const useFetchModulationLocationContracts = (
  request: FetchModulationLocationContractsOptions,
  queryOptions?: UseAppQueryOptions<ModulationLocationContractSchema[]>
) => {
  return useQuery({
    ...queryOptions,
    queryKey: modulationLocationContractsCachekeys.all(
      // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
      request?.queryParams?.location_id
    ),
    queryFn: () => fetchModulationLocationContracts(request)
  })
}

/**
 * Fetch modulation location contracts filtered by current date
 */
export const useFetchCurrentModulationLocationContracts = (
  request: FetchModulationLocationContractsOptions,
  queryOptions?: UseAppQueryOptions<ModulationLocationContractSchema[]>
) =>
  useFetchModulationLocationContracts(request, {
    ...queryOptions,
    select: data =>
      data?.filter(
        (mlc: ModulationLocationContractSchema) =>
          moment().isSameOrAfter(mlc.interval_begin) &&
          moment().isSameOrBefore(mlc.interval_end)
      )
  })?.data?.[0]

/**
 * Fetch modulation location contracts filtered by past date
 */
export const useFetchPastModulationLocationContracts = (
  request: FetchModulationLocationContractsOptions,
  queryOptions?: UseAppQueryOptions<ModulationLocationContractSchema[]>
) =>
  useFetchModulationLocationContracts(request, {
    ...queryOptions,
    select: data =>
      data?.filter((mlc: ModulationLocationContractSchema) =>
        moment().isAfter(mlc.interval_end)
      )
  })?.data

/**
 * Fetch modulation location contracts filtered by future date
 */
export const useFetchFutureModulationLocationContracts = (
  request: FetchModulationLocationContractsOptions,
  queryOptions?: UseAppQueryOptions<ModulationLocationContractSchema[]>
) =>
  useFetchModulationLocationContracts(request, {
    ...queryOptions,
    select: data =>
      data?.filter((mlc: ModulationLocationContractSchema) =>
        moment().isBefore(mlc.interval_begin)
      )
  })?.data
